/**
 * Home Groups page: config from API (hero, text, image) + list of home groups.
 */

import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { ListSkeleton } from "../../components/ui/Skeleton.tsx";
import { IconHomeHeart } from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import { useHomeGroups, useHomeGroupsPageConfig } from "./hooks";
import { sanitizeHtml } from "@/lib/sanitize";

const DAY_OF_WEEK_LABELS: Record<string, string> = {
  MONDAY: "Пн",
  TUESDAY: "Вт",
  WEDNESDAY: "Ср",
  THURSDAY: "Чт",
  FRIDAY: "Пт",
  SATURDAY: "Сб",
  SUNDAY: "Вс",
};

function formatLeaderName(leader: {
  firstName: string | null;
  lastName: string | null;
}): string {
  const parts = [leader.firstName, leader.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "Лидер";
}

function formatSchedule(
  schedules: Array<{ dayOfWeek: string; startTime: string; isActive: boolean }>
): string {
  const active = schedules.filter((s) => s.isActive).slice(0, 3);
  return active
    .map(
      (s) => `${DAY_OF_WEEK_LABELS[s.dayOfWeek] ?? s.dayOfWeek} ${s.startTime}`
    )
    .join(", ");
}

function buildImageUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  if (url.startsWith("http")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

export function HomeGroupsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  // URQL hooks
  const {
    config,
    isLoading: isLoadingConfig,
    error: configError,
    refetch: refetchConfig,
  } = useHomeGroupsPageConfig();
  const {
    homeGroups: groups,
    isLoading: isLoadingGroups,
    error: groupsError,
    refetch: refetchGroups,
  } = useHomeGroups({ filter: { isOpen: true } });

  const error = configError?.message ?? groupsError?.message ?? null;

  const handleRetry = () => {
    refetchConfig();
    refetchGroups();
  };

  // BackButton (Mini App / PWA: назад)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={handleRetry}
          className="mt-6 border border-slate-100 ring-1 ring-slate-200/50"
        />
      </Layout>
    );
  }

  const title = config?.title?.trim() || "Домашние группы";
  const bodyText = config?.bodyText?.trim();
  const imageUrl = buildImageUrl(config?.imageUrl);

  return (
    <Layout>
      {/* Hero: картинка как в мероприятиях (aspect 4/3, rounded) или текст */}
      {imageUrl ? (
        <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : null}
      {(!imageUrl || bodyText) && (
        <div
          className={`rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 ${
            imageUrl ? "mb-4" : ""
          }`}
        >
          <div className="p-6 xs:p-8">
            <div className="flex items-start gap-3">
              {!imageUrl && (
                <div className="w-12 h-12 rounded-xl bg-rose-50/80 flex items-center justify-center text-rose-600/75 flex-shrink-0">
                  <IconHomeHeart className="w-6 h-6" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                {!imageUrl && (
                  <h1 className="text-xl xs:text-2xl font-bold text-slate-900">
                    {title}
                  </h1>
                )}
                {bodyText && (
                  <div
                    className={`prose prose-slate prose-sm max-w-none text-slate-700 whitespace-pre-wrap ${
                      !imageUrl ? "mt-3" : ""
                    }`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyText) }}
                  />
                )}
                {!bodyText && !isLoadingConfig && !imageUrl && (
                  <p className="text-sm text-slate-600 mt-0.5">
                    Небольшие встречи для общения и изучения Библии
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Секция групп */}
      <section className="mt-6">
        <p className="section-title px-0">Открытые группы</p>
        {isLoadingGroups ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-6">
            <ListSkeleton count={3} />
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-8 text-center">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">
              Скоро
            </p>
            <p className="text-slate-600 mt-1">Группы появятся здесь</p>
          </div>
        ) : (
          <div className="space-y-3 rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-4">
            {groups.map((group) => {
              const scheduleStr = formatSchedule(group.schedules);
              const leaderName = formatLeaderName(group.leader);
              const leftLabel = scheduleStr || `${group.memberCount} чел.`;
              return (
                <Link
                  key={group.id}
                  to={`/home-group/${group.id}`}
                  className="block rounded-xl lg-mobile:rounded-2xl overflow-hidden bg-white/95 border shadow-sm transition-all active:scale-[0.99] border-slate-100"
                >
                  <div className="flex">
                    <div
                      className="flex-shrink-0 w-1.5 rounded-l-xl lg-mobile:rounded-l-2xl bg-rose-400/60"
                      aria-hidden
                    />
                    <div className="relative flex-shrink-0 w-16 xs:w-20 lg-mobile:w-24 flex flex-col items-center justify-center gap-1 py-3 xs:py-4 lg-mobile:py-5 border-r border-slate-100 overflow-hidden bg-rose-50/30">
                      <IconHomeHeart className="w-4 h-4 xs:w-5 xs:h-5 lg-mobile:w-6 lg-mobile:h-6 text-rose-600/75" />
                      <span className="text-xs xs:text-sm lg-mobile:text-base font-semibold text-slate-700 text-center leading-tight line-clamp-2 px-1">
                        {leftLabel}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 p-3 xs:p-4 lg-mobile:p-5">
                      <div className="flex items-start gap-2 mb-1 lg-mobile:mb-2">
                        <h3 className="text-sm xs:text-base lg-mobile:text-lg font-semibold text-slate-900 line-clamp-2 flex-1 min-w-0">
                          {group.name}
                        </h3>
                        {group.isOpen && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600 bg-emerald-50 rounded">
                            Открыта
                          </span>
                        )}
                      </div>
                      <p className="text-xs xs:text-sm lg-mobile:text-base text-slate-500 mb-2 lg-mobile:mb-3">
                        {leaderName}
                        {group.location && ` · ${group.location}`}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs xs:text-sm lg-mobile:text-base text-slate-400">
                          {group.memberCount} участников
                        </span>
                        <span className="text-xs xs:text-sm lg-mobile:text-base text-slate-400">
                          Подробнее →
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <div className="h-4" />
    </Layout>
  );
}
